import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import { cashPriceFromMrp, getProductDiscount } from '../utils/cashPricing';

const ShopContext = createContext(null);

const WISHLIST_KEY = 'rahber_showroom_wishlist_v1';
const CART_KEY = 'rahber_showroom_cart_v1';

function loadWishlist() {
    try {
        const raw = localStorage.getItem(WISHLIST_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function loadCart() {
    try {
        const raw = localStorage.getItem(CART_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function buildProductSnapshot(p) {
    if (!p) return null;
    const mrp = Number(p.mrp || p.price || 0);
    const instantCashDiscount =
        p.instant_cash_discount ??
        p.instantCashDiscount ??
        p.cash_discount_percent ??
        p.cashDiscountPercent ??
        null;
    const discount = getProductDiscount(p);
    const cash = mrp > 0 ? cashPriceFromMrp(mrp, discount) : 0;
    const id = String(p._id || p.id || '');
    return {
        id,
        _id: id,
        name: p.name || 'Genuine Home Appliance',
        brand: typeof p.brand === 'object' ? p.brand?.name : (p.brand || 'Rahber Corporation'),
        category: typeof p.category === 'object' ? p.category?.name : (p.category || ''),
        image: p.image || (Array.isArray(p.images) && p.images[0]) || p.secondary_image || '',
        mrp,
        cashPrice: cash,
        instant_cash_discount: instantCashDiscount,
        monthlyInst: p.monthlyInst || 0,
        slug: p.slug || id,
    };
}

export function ShopProvider({ children }) {
    const [wishlist, setWishlist] = useState(loadWishlist);
    const [cart, setCart] = useState(loadCart);
    const [activeCartModal, setActiveCartModal] = useState(null);
    const [activeWishlistModal, setActiveWishlistModal] = useState(null);

    useEffect(() => {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
    }, [wishlist]);

    useEffect(() => {
        localStorage.setItem(CART_KEY, JSON.stringify(cart));
    }, [cart]);

    const wishlistCount = wishlist.length;
    const cartItemCount = useMemo(
        () => cart.reduce((sum, item) => sum + (item.quantity || 0), 0),
        [cart]
    );

    const isInWishlist = useCallback(
        (productId) => wishlist.some((w) => w.id === String(productId)),
        [wishlist]
    );

    const closeWishlistModal = useCallback(() => {
        setActiveWishlistModal(null);
    }, []);

    const toggleWishlist = useCallback((product) => {
        const snapshot = buildProductSnapshot(product);
        if (!snapshot) return;
        setWishlist((prev) => {
            const exists = prev.some((w) => w.id === snapshot.id);
            if (exists) {
                return prev.filter((w) => w.id !== snapshot.id);
            }
            // Trigger animation when added to wishlist
            setActiveWishlistModal({
                isOpen: true,
                product: snapshot,
            });
            return [...prev, snapshot];
        });
    }, []);

    const removeFromWishlist = useCallback((id) => {
        setWishlist((prev) => prev.filter((w) => w.id !== String(id)));
    }, []);

    const closeCartModal = useCallback(() => {
        setActiveCartModal(null);
    }, []);

    const addToCart = useCallback((product, quantity = 1) => {
        const snapshot = buildProductSnapshot(product);
        if (!snapshot) return;
        const qty = Math.max(1, parseInt(quantity, 10) || 1);
        setCart((prev) => {
            const idx = prev.findIndex((c) => c.id === snapshot.id);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = {
                    ...next[idx],
                    quantity: next[idx].quantity + qty,
                };
                return next;
            }
            return [...prev, { ...snapshot, quantity: qty }];
        });

        // Trigger the center Add to Cart popup animation
        setActiveCartModal({
            isOpen: true,
            product: snapshot,
            quantity: qty,
        });
    }, []);

    const updateCartQuantity = useCallback((id, quantity) => {
        const q = parseInt(quantity, 10);
        if (Number.isNaN(q) || q < 1) return;
        setCart((prev) =>
            prev.map((c) => (c.id === String(id) ? { ...c, quantity: q } : c))
        );
    }, []);

    const removeFromCart = useCallback((id) => {
        setCart((prev) => prev.filter((c) => c.id !== String(id)));
    }, []);

    const clearCart = useCallback(() => {
        setCart([]);
    }, []);

    const value = useMemo(
        () => ({
            wishlist,
            cart,
            activeCartModal,
            closeCartModal,
            activeWishlistModal,
            closeWishlistModal,
            wishlistCount,
            cartItemCount,
            isInWishlist,
            toggleWishlist,
            removeFromWishlist,
            addToCart,
            updateCartQuantity,
            removeFromCart,
            clearCart,
        }),
        [
            wishlist,
            cart,
            activeCartModal,
            closeCartModal,
            activeWishlistModal,
            closeWishlistModal,
            wishlistCount,
            cartItemCount,
            isInWishlist,
            toggleWishlist,
            removeFromWishlist,
            addToCart,
            updateCartQuantity,
            removeFromCart,
            clearCart,
        ]
    );

    return (
        <ShopContext.Provider value={value}>{children}</ShopContext.Provider>
    );
}

export function useShop() {
    const ctx = useContext(ShopContext);
    if (!ctx) {
        throw new Error('useShop must be used within ShopProvider');
    }
    return ctx;
}
